import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Slide, Stack } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { confirmAlert } from "react-confirm-alert";
import { ApiError } from "../../Api/ApiError";
import { deleteCustomerData, deleteMypage, getCustomerDetail } from "../../Api/Customer";
import { CustomerDetailInterface } from "../../Objects/Customer";
import { UserObjectStore } from "../../ObjectStore/UserObjectStore";
import CustomerDetailCard from "../molecules/CustomerDetailCard";
import { CouponDataEditDialog } from "./CouponDataEditDialog";
import { CustomerDataEditDialog } from "./CustomerDataEditDialog";
import { CustomerMemoEditDialog } from "./CustomerMemoEditDialog";
import { MemeberInfoEditDialog } from "./MemeberInfoEditDialog";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface CustomerDetailDialogProps {
    ctmId?: number
    onClose: (isEdit: boolean) => void
}

const CustomerDetailDialog: React.FC<CustomerDetailDialogProps> = ({
    ctmId,
    onClose,
}) => {
    let { enqueueSnackbar } = useSnackbar()
    const { userObjectState } = React.useContext(UserObjectStore)

    const [open, setOpen] = React.useState<boolean>(false)
    const [customerDetailData, setCustomerDetailData] = React.useState<CustomerDetailInterface>()

    const [editMemberDialogOpen, setEditMemberDialogOpen] = React.useState<boolean>(false)
    const [editCustomerDataDialogOpen, setEditCustomerDataDialogOpen] = React.useState<boolean>(false)
    const [editCouponDataDialogOpen, setEditCouponDataDialogOpen] = React.useState<boolean>(false)
    const [editCustomerMemoDialogOpen, setEditCustomerMemoDialogOpen] = React.useState<boolean>(false)

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadData = React.useCallback((callback?: () => void) => {
        if (ctmId) {
            getCustomerDetail({
                accessKey: "HEAD-OFFICE_LOGIN",
                ctmId: ctmId
            }, {signal: abortController.signal}).then(result => {
                setCustomerDetailData(result)
                if (callback) {
                    callback()
                }
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, ctmId, enqueueSnackbar])

    const doDeleteMypage = React.useCallback(() => {
        if (ctmId) {
            deleteMypage({
                ctm_id: ctmId
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("マイページ登録を解除しました", { variant: "success" })
                loadData()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, ctmId, enqueueSnackbar, loadData])

    const doDeleteCustomerData = React.useCallback(() => {
        if (ctmId) {
            deleteCustomerData({
                ctm_id: ctmId
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("顧客データを抹消しました", { variant: "success" })
                loadData()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, ctmId, enqueueSnackbar, loadData])

    /**
     * データ初期化
     */
    React.useEffect(() => {
        setOpen(false)
        loadData(() => {
            setOpen(true)
        })
    }, [loadData])

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={() => {
                setOpen(false)
            }}
            TransitionProps={{
                onExited: () => {
                    onClose(false)
                }
            }}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>顧客データ詳細</DialogTitle>
            <DialogContent>
                <Grid container
                    spacing={2}
                    direction={{
                        xs: "column",
                        sm: "row-reverse"
                    }}
                >
                    <Grid item xs={4} md={3}>
                        <Box>
                            <Stack
                                spacing={{
                                    xs: 1,
                                    sm: 2
                                }}
                            >
                                {customerDetailData?.delete_flug !== "1" &&
                                    <>
                                        {userObjectState.stfAccessList.includes("CustomerDataEdit") &&
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setEditMemberDialogOpen(true)
                                                }}
                                            >会員情報編集</Button>
                                        }
                                        {userObjectState.stfAccessList.includes("CustomerDataEdit") &&
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setEditCustomerDataDialogOpen(true)
                                                }}
                                            >顧客データ編集</Button>
                                        }
                                        {userObjectState.stfAccessList.includes("CustomerDataEdit") &&
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setEditCouponDataDialogOpen(true)
                                                }}
                                            >ポイント編集</Button>
                                        }
                                        {userObjectState.stfAccessList.includes("CustomerDataEdit") &&
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setEditCustomerMemoDialogOpen(true)
                                                }}
                                            >メモ編集</Button>
                                        }
                                        {customerDetailData?.mypage_flug === "1" &&
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    confirmAlert({
                                                        title: "マイページ登録の解除",
                                                        message: "マイページ登録の解除をします。よろしいですか？",
                                                        buttons: [
                                                            {
                                                                label: "解除実行",
                                                                onClick: () => {
                                                                    doDeleteMypage()
                                                                }
                                                            }, {
                                                                label: "キャンセル",
                                                                onClick: () => { }
                                                            }
                                                        ]
                                                    })
                                                }}
                                            >マイページ登録解除</Button>
                                        }
                                        {userObjectState.stfAccessList.includes("CustomerDataDelete") &&
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => {
                                                    confirmAlert({
                                                        title: "顧客データ抹消",
                                                        message: "顧客データを抹消をします。よろしいですか？",
                                                        buttons: [
                                                            {
                                                                label: "抹消実行",
                                                                onClick: () => {
                                                                    doDeleteCustomerData()
                                                                }
                                                            }, {
                                                                label: "キャンセル",
                                                                onClick: () => { }
                                                            }
                                                        ]
                                                    })
                                                }}
                                            >顧客データ抹消</Button>
                                        }
                                    </>
                                }
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setOpen(false)
                                    }}
                                >閉じる</Button>
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item xs={8} md={9}>
                        {customerDetailData &&
                            <CustomerDetailCard
                                customerData={customerDetailData}
                            />
                        }
                    </Grid>
                </Grid>
                <MemeberInfoEditDialog
                    ctmId={editMemberDialogOpen ? ctmId : undefined}
                    onClose={() => {
                        loadData()
                        setEditMemberDialogOpen(false)
                    }}
                    dialogProps={{
                        fullWidth: true,
                        hideBackdrop: true,
                    }}
                />
                <CustomerDataEditDialog
                    ctmId={editCustomerDataDialogOpen ? ctmId : undefined}
                    onClose={() => {
                        loadData()
                        setEditCustomerDataDialogOpen(false)
                    }}
                    dialogProps={{
                        fullWidth: true,
                        hideBackdrop: true,
                    }}
                />
                <CouponDataEditDialog
                    ctmId={editCouponDataDialogOpen ? ctmId : undefined}
                    onClose={() => {
                        loadData()
                        setEditCouponDataDialogOpen(false)
                    }}
                    dialogProps={{
                        fullWidth: true,
                        hideBackdrop: true,
                    }}
                />
                <CustomerMemoEditDialog
                    ctmId={editCustomerMemoDialogOpen ? ctmId : undefined}
                    onClose={() => {
                        loadData()
                        setEditCustomerMemoDialogOpen(false)
                    }}
                    dialogProps={{
                        fullWidth: true,
                        hideBackdrop: true,
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

export default CustomerDetailDialog